import { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../hooks/use-theme";
import { productPath } from "../lib/catalog";
import { cx, formatCurrency, parseTestedWith, toDarkModeImageUrl, toSentenceCase } from "../lib/utils";

type ProductCardProps = {
  id: number;
  name: string;
  slug: string;
  price: number;
  category: string;
  description: string;
  image?: string | null;
  quantity?: number;
  testedWith?: string | null;
  cartQuantity?: number;
  onAddToCart?: (productId: number) => Promise<void>;
};

export function ProductCard(props: ProductCardProps) {
  const [adding, setAdding] = useState(false);
  const theme = useTheme();
  const compatibilityTags = parseTestedWith(props.testedWith);
  const productName = toSentenceCase(props.name);
  const inStock = Boolean(props.quantity && props.quantity > 0);
  const isDark = theme === "dark";
  const displayImage = theme === "dark" ? toDarkModeImageUrl(props.image) : props.image;
  const lowStock = inStock && Boolean(props.quantity && props.quantity <= 5);

  async function handleAddToCart() {
    if (!props.onAddToCart || !inStock || adding) {
      return;
    }

    setAdding(true);
    try {
      await props.onAddToCart(props.id);
    } finally {
      setAdding(false);
    }
  }

  function CartBadge({ quantity }: { quantity: number }) {
    return (
      <Link
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-nv-electric/15 bg-nv-electric/6 text-nv-electric transition hover:border-nv-electric/30 hover:bg-nv-electric/10"
        title="View cart"
        to="/shop/cart"
      >
        <svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
          <path d="M3 4h2l2.1 9.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.8L20 7H7.2" />
          <circle cx="10" cy="19" r="1.5" />
          <circle cx="17" cy="19" r="1.5" />
        </svg>
        <span className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-nv-accent px-1 text-[10px] font-bold leading-none text-white">
          {quantity}
        </span>
      </Link>
    );
  }

  return (
    <article
      className={cx(
        "group relative flex h-full flex-col overflow-hidden rounded-[30px] border shadow-[0_24px_70px_rgba(16,32,51,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_28px_80px_rgba(29,78,216,0.14)]",
        isDark
          ? "border-white/10 bg-[linear-gradient(180deg,rgba(11,26,49,0.94),rgba(8,19,36,0.96))] text-white"
          : "border-black/8 bg-white text-nv-ink"
      )}
    >
      {isDark ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.16),transparent_48%),radial-gradient(circle_at_top_right,rgba(59,130,246,0.14),transparent_42%)]" />
      ) : null}
      <div className={cx("relative overflow-hidden", isDark ? "bg-transparent" : "bg-white")}>
        {displayImage ? (
          <img
            alt={productName}
            className="h-56 w-full object-cover transition duration-300 group-hover:scale-[1.04]"
            loading="lazy"
            src={displayImage}
          />
        ) : (
          <div
            className={cx(
              "grid h-56 place-items-center text-sm font-medium text-nv-slate",
              isDark
                ? "bg-[linear-gradient(135deg,rgba(34,211,238,0.1),rgba(37,99,235,0.16))]"
                : "bg-[linear-gradient(135deg,rgba(29,78,216,0.08),rgba(255,209,102,0.12))]"
            )}
          >
            Image coming soon
          </div>
        )}
      </div>
      <div className={cx("relative flex flex-1 flex-col p-5", isDark ? "bg-transparent" : "bg-white")}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p
                className={cx(
                  "break-words rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em]",
                  isDark ? "bg-cyan-400/10 text-cyan-300" : "bg-nv-electric/8 text-nv-electric"
                )}
              >
                {props.category}
              </p>
              {lowStock ? (
                <span
                  className={cx(
                    "rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]",
                    isDark
                      ? "border-amber-900/70 bg-amber-950/40 text-amber-300"
                      : "border-amber-200 bg-amber-50 text-amber-700"
                  )}
                >
                  Low stock
                </span>
              ) : null}
            </div>
            <h3 className={cx("mt-3 break-words text-[1.35rem] font-semibold leading-tight tracking-tight", isDark ? "text-white" : "text-nv-ink")}>
              <Link
                className={cx("transition hover:text-nv-electric", isDark ? "text-white" : "text-nv-ink")}
                title="View item"
                to={productPath(props.slug, props.id)}
              >
                {productName}
              </Link>
            </h3>
          </div>
          <div
            className={cx(
              "shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-center text-xs font-semibold",
              inStock
                ? isDark
                  ? "border border-emerald-900/70 bg-emerald-950/40 text-emerald-300"
                  : "border border-emerald-200 bg-emerald-50 text-emerald-700"
                : isDark
                  ? "border border-red-900/70 bg-red-950/40 text-red-300"
                  : "border border-red-200 bg-red-50 text-red-700"
            )}
          >
            {inStock ? "Ready" : "Out"}
          </div>
        </div>
        <p className={cx("mt-4 line-clamp-3 text-sm leading-6", isDark ? "text-slate-300" : "text-nv-slate")}>{props.description}</p>
        {compatibilityTags.length > 0 ? (
          <div className="mt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-nv-slate">
              Tested with
            </p>
            <div className="flex flex-wrap gap-2">
              {compatibilityTags.map((tag) => (
                <span
                  className={cx(
                    "rounded-full border px-3 py-1 text-xs font-semibold",
                    isDark
                      ? "border-white/10 bg-white/6 text-slate-200"
                      : "border-black/8 bg-white/70 text-nv-ink"
                  )}
                  key={tag}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ) : null}
        <div className="mt-auto flex items-end justify-between gap-4 pt-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-nv-slate">Price</p>
            <p className={cx("mt-2 text-3xl font-bold tracking-tight", isDark ? "text-white" : "text-nv-ink")}>{formatCurrency(props.price)}</p>
          </div>
          <div className="flex items-center gap-2">
            {props.cartQuantity && props.cartQuantity > 0 ? (
              <CartBadge quantity={props.cartQuantity} />
            ) : null}
            <button
              className="brand-button inline-flex min-w-[132px] items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-nv-electric/40 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!inStock || adding}
              onClick={() => void handleAddToCart()}
              type="button"
            >
              {adding ? "Adding..." : inStock ? "Add to cart" : "Out of stock"}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
